// API Response Types
export interface ApiResponse<T> {
  data?: T;
  error?: string;
  message?: string;
}

// Product Variant Type
export interface ProductVariant {
  variantId: number;
  title?: string;
  sku?: string;
  price: number;
  compareAtPrice?: number;
  available: boolean;
  option1?: string;
  option2?: string;
  option3?: string;
}

// Recommendation Types
export interface ProductRecommendation {
  productId: number;
  handle: string;
  title: string;
  imageUrl?: string;
  price: number;
  compareAtPrice?: number;
  score: number;
  reason?: string;
  recommendationType: string;
  // Variant information
  variantId?: number;
  variants?: ProductVariant[];
}

export interface RecommendationsResponse {
  shopDomain: string;
  sourceProductId?: number;
  recommendations: ProductRecommendation[];
  generatedAt: string;
  isCached: boolean;
}

// Settings Types
export interface DisplaySettings {
  showOnProductPage: boolean;
  showOnCartPage: boolean;
  showOnHomePage: boolean;
  maxRecommendations: number;
  widgetTitle: string;
  widgetStyle: 'grid' | 'carousel' | 'list';
  recommendationType: 'similar' | 'trending' | 'personalized' | 'frequently-bought' | 'recently-viewed';
  // Cart widget settings
  cartWidgetEnabled: boolean;
  cartWidgetTitle: string;
  cartWidgetType: 'similar' | 'trending' | 'personalized' | 'frequently-bought';
  // Low stock indicator settings
  showLowStockIndicator: boolean;
  lowStockThreshold: number;
  lowStockText: string;
}

export interface AISettings {
  strategy: 'collaborative' | 'content-based' | 'hybrid';
  minConfidence: number;
  enablePersonalization: boolean;
}

export interface DataCollectionSettings {
  trackProductViews: boolean;
  trackAddToCart: boolean;
  trackPurchases: boolean;
  trackSearches: boolean;
}

// Widget Customization Types
export interface WidgetCustomization {
  // Colors
  primaryColor: string;
  backgroundColor: string;
  textColor: string;
  priceColor: string;
  badgeBackgroundColor: string;
  badgeTextColor: string;

  // Typography
  titleFontSize: string;
  productTitleFontSize: string;
  priceFontSize: string;
  fontFamily: string;

  // Spacing and borders
  borderRadius: string;
  cardBorderColor: string;
  cardShadow: boolean;
  cardPadding: string;

  // Layout
  gridGap: string;
  cardMinWidth: string;

  // Badge
  showBadge: boolean;
  badgeText: string;

  // Image
  imageAspectRatio: string;
  imageObjectFit: 'cover' | 'contain' | 'fill';
}

export const defaultWidgetCustomization: WidgetCustomization = {
  primaryColor: '#0070f3',
  backgroundColor: '#ffffff',
  textColor: '#333333',
  priceColor: '#333333',
  badgeBackgroundColor: '#e0f0ff',
  badgeTextColor: '#0070f3',
  titleFontSize: '1.5rem',
  productTitleFontSize: '0.9rem',
  priceFontSize: '1rem',
  fontFamily: 'inherit',
  borderRadius: '8px',
  cardBorderColor: '#e5e5e5',
  cardShadow: true,
  cardPadding: '12px',
  gridGap: '20px',
  cardMinWidth: '200px',
  showBadge: true,
  badgeText: 'Recommended for you',
  imageAspectRatio: '1',
  imageObjectFit: 'cover',
};

// Email Settings Types
export interface EmailSettings {
  abandonedCartEnabled: boolean;
  abandonedCartDelay: '1h' | '4h' | '24h';
  abandonedCartSubject: string;
  postPurchaseEnabled: boolean;
  postPurchaseDelay: '1d' | '3d' | '7d';
  postPurchaseSubject: string;
}

// Collection Settings Types
export interface CollectionSettings {
  excludedCollections: string[];
  priorityCollections: string[];
}

// Setup Status Types
export interface SetupStatus {
  isInstalled: boolean;
  hasBehaviorData: boolean;
  totalBehaviors: number;
  lastBehaviorAt?: string;
}

export interface ShopSettings {
  shopDomain: string;
  displaySettings: DisplaySettings;
  aiSettings: AISettings;
  dataCollectionSettings: DataCollectionSettings;
  widgetCustomization?: WidgetCustomization;
  emailSettings?: EmailSettings;
  collectionSettings?: CollectionSettings;
  recommendationWeights?: RecommendationWeights;
  bundleSettings?: BundleSettings;
  chatWidgetEnabled?: boolean;
  analytics?: AnalyticsOverview;
  setupStatus?: SetupStatus;
  showTestPanel?: boolean;
  sessionScoring?: SessionScoringSettings;
  popupSettings?: MerchantPopupSettings;
  cartDrawerSettings?: CartDrawerSettings;
}

// Analytics Types
export interface AnalyticsOverview {
  totalProductViews: number;
  totalAddToCarts: number;
  totalPurchases: number;
  recommendationClicks: number;
  conversionRate: number;
  lastUpdated: string;
}

// Shop Types
export interface PlanFeatures {
  planName: string;
  planLevel: number;
  monthlyOrderLimit: number;
  hasBundles: boolean;
  hasFrequentlyBoughtTogether: boolean;
  hasAdvancedAnalytics: boolean;
  hasAiChat: boolean;
  hasSocialProof: boolean;
  hasEmailCampaigns: boolean;
  hasCustomMlTraining: boolean;
  upgradePlanName?: string;
  upgradePlanPrice?: number;
}

export interface ShopInfo {
  shopDomain: string;
  name?: string;
  email?: string;
  isActive: boolean;
  installedAt: string;
  hasSettings: boolean;
  totalBehaviorsTracked: number;
  currency: string;
  shouldShowReviewPrompt: boolean;
  appStoreReviewUrl?: string;
  planName?: string;
  planFeatures?: PlanFeatures;
}

// Billing Types
export interface BillingPlan {
  name: string;
  price: number;
  features: string[];
  recommended?: boolean;
}

export interface BillingResponse {
  planName: string;
  price: number;
  billingInterval: string;
  confirmationUrl?: string;
  isActive: boolean;
}

// ==================== RECOMMENDATION WEIGHTS TYPES ====================

/**
 * Context weights for a specific page/location.
 * All weights should sum to 100 when using "mix" mode.
 */
export interface ContextWeights {
  /** Weight for Similar Products (0-100) */
  similar: number;
  /** Weight for Frequently Bought Together (0-100) */
  frequentlyBoughtTogether: number;
  /** Weight for Trending products (0-100) */
  trending: number;
  /** Weight for Personalized recommendations (0-100) */
  personalized: number;
  /** Weight for New Arrivals (0-100) */
  newArrivals: number;
  /** Mode: "similar", "fbt", "trending", "personalized", "newarrivals", "mix" */
  mode: 'similar' | 'fbt' | 'trending' | 'personalized' | 'newarrivals' | 'mix';
  /** Custom title for this context (localized) */
  title: string;
  /** English title fallback */
  titleEn: string;
  /** Whether this context is enabled */
  enabled: boolean;
}

/**
 * Toggle to enable/disable specific recommendation types globally.
 */
export interface EnabledTypes {
  similar: boolean;
  frequentlyBoughtTogether: boolean;
  trending: boolean;
  personalized: boolean;
  newArrivals: boolean;
  bestSellers: boolean;
}

/**
 * Full recommendation weights configuration for all contexts.
 */
export interface RecommendationWeights {
  /** Product page recommendations */
  productPage?: ContextWeights;
  /** Home page recommendations */
  homePage?: ContextWeights;
  /** Cart page recommendations */
  cartPage?: ContextWeights;
  /** "Recommended for you" widget */
  recommendedForYou?: ContextWeights;
  /** Post-purchase / Thank you page */
  postPurchase?: ContextWeights;
  /** Email recommendations */
  email?: ContextWeights;
  /** Global aggressiveness slider (0-100) */
  globalAggressiveness?: number;
  /** Discovery vs Personalization slider (0-100) */
  discoveryLevel?: number;
  /** Enable/disable specific recommendation types globally */
  enabledTypes?: EnabledTypes;
  /** Fallback type when not enough data */
  fallbackType?: 'trending' | 'bestsellers' | 'newarrivals' | 'random' | 'hide';
  /** Minimum number of recommendations to show */
  minRecommendations?: number;
}

/**
 * Default recommendation weights configuration.
 */
export const defaultRecommendationWeights: RecommendationWeights = {
  productPage: {
    similar: 35,
    frequentlyBoughtTogether: 40,
    trending: 15,
    personalized: 10,
    newArrivals: 0,
    mode: 'mix',
    title: 'Potser t\'agrada',
    titleEn: 'You might also like',
    enabled: true,
  },
  homePage: {
    similar: 0,
    frequentlyBoughtTogether: 0,
    trending: 50,
    personalized: 40,
    newArrivals: 10,
    mode: 'mix',
    title: 'Recomanats per a tu',
    titleEn: 'Recommended for you',
    enabled: true,
  },
  cartPage: {
    similar: 25,
    frequentlyBoughtTogether: 65,
    trending: 10,
    personalized: 0,
    newArrivals: 0,
    mode: 'mix',
    title: 'Completa la teva compra',
    titleEn: 'Complete your purchase',
    enabled: true,
  },
  recommendedForYou: {
    similar: 20,
    frequentlyBoughtTogether: 0,
    trending: 30,
    personalized: 50,
    newArrivals: 0,
    mode: 'mix',
    title: 'Fet per a tu',
    titleEn: 'Made for you',
    enabled: true,
  },
  postPurchase: {
    similar: 30,
    frequentlyBoughtTogether: 40,
    trending: 20,
    personalized: 10,
    newArrivals: 0,
    mode: 'mix',
    title: 'Clients com tu també han comprat',
    titleEn: 'Customers like you also bought',
    enabled: true,
  },
  email: {
    similar: 40,
    frequentlyBoughtTogether: 35,
    trending: 15,
    personalized: 10,
    newArrivals: 0,
    mode: 'mix',
    title: 'Recomanacions per a tu',
    titleEn: 'Recommendations for you',
    enabled: true,
  },
  globalAggressiveness: 60,
  discoveryLevel: 40,
  enabledTypes: {
    similar: true,
    frequentlyBoughtTogether: true,
    trending: true,
    personalized: true,
    newArrivals: true,
    bestSellers: true,
  },
  fallbackType: 'trending',
  minRecommendations: 2,
};

// ==================== BUNDLE TYPES ====================

/**
 * Product in a bundle
 */
export interface BundleProduct {
  productId: number;
  variantId?: number;
  title: string;
  handle?: string;
  imageUrl?: string;
  price: number;
  compareAtPrice?: number;
  quantity: number;
  isAnchor: boolean;
}

/**
 * Bundle response from API
 */
export interface ProductBundle {
  id: string;
  name: string;
  description?: string;
  products: BundleProduct[];
  discountType: 'percentage' | 'fixed_amount' | 'fixed_price';
  discountValue: number;
  originalPrice: number;
  bundlePrice: number;
  savings: number;
  savingsPercentage: number;
  bundleType: 'fbt' | 'cross_sell' | 'upsell' | 'custom';
  imageUrl?: string;
  score: number;
  isActive?: boolean;
  shopifyDiscountCode?: string;
}

/**
 * Bundle settings configuration
 */
export interface BundleSettings {
  autoGenerateEnabled: boolean;
  defaultDiscountPercentage: number;
  minFbtScore: number;
  maxProductsPerBundle: number;
  minProductsPerBundle: number;
  maxBundlesPerProduct: number;
  minBundlePrice?: number;
  maxBundlePrice?: number;
  displayStyle: 'inline' | 'popup' | 'drawer';
  showSavingsBadge: boolean;
  bundleCtaText: string;
  autoDiscountMode?: 'auto' | 'manual' | 'off';
  perProductDiscountOverrides?: Record<string, string>;
  autoDiscountType?: string;
  autoDiscountValue?: number;
}

/**
 * Default bundle settings
 */
export const defaultBundleSettings: BundleSettings = {
  autoGenerateEnabled: true,
  defaultDiscountPercentage: 10,
  minFbtScore: 0.3,
  maxProductsPerBundle: 4,
  minProductsPerBundle: 2,
  maxBundlesPerProduct: 3,
  displayStyle: 'inline',
  showSavingsBadge: true,
  bundleCtaText: 'Add Bundle to Cart',
  autoDiscountMode: 'manual',
  autoDiscountType: 'percentage',
  autoDiscountValue: 10,
  perProductDiscountOverrides: {},
};

/**
 * Response with bundles list
 */
export interface BundlesResponse {
  shopDomain: string;
  productId?: number;
  bundles: ProductBundle[];
  totalCount: number;
  page: number;
  pageSize: number;
  totalPages: number;
  generatedAt?: string;
}

/**
 * Request to create or update a bundle
 */
export interface BundleFormData {
  name: string;
  description?: string;
  products: BundleProduct[];
  discountType: 'percentage' | 'fixed_amount' | 'fixed_price';
  discountValue: number;
  bundleType: 'fbt' | 'cross_sell' | 'upsell' | 'custom';
  isActive: boolean;
}

// ============================================
// Chat Widget Settings Types
// ============================================

export interface ChatWidgetSettings {
  // Enable/disable
  enabled: boolean;

  // Position: bottom-right, bottom-left, top-right, top-left
  position: 'bottom-right' | 'bottom-left' | 'top-right' | 'top-left';

  // Size: small, medium, large
  size: 'small' | 'medium' | 'large';

  // Colors
  primaryColor: string;
  secondaryColor: string;
  backgroundColor: string;
  textColor: string;
  userMessageColor: string;
  assistantMessageColor: string;

  // Icon
  iconType: 'chat' | 'robot' | 'help' | 'custom';
  customIconUrl?: string;

  // Branding
  headerTitle: string;
  showBranding: boolean;

  // Welcome message
  welcomeMessage: string;

  // Placeholder
  inputPlaceholder: string;

  // Behavior
  autoOpen: boolean;
  autoOpenDelay: number;
  showOnMobile: boolean;
  mobileBreakpoint: number;
  soundEnabled: boolean;

  // Animation
  animationType: 'slide' | 'fade' | 'bounce' | 'none';

  // Style
  borderRadius: string;
  zIndex: number;
  offsetX: number;
  offsetY: number;
  windowWidth: number;
  windowHeight: number;
  buttonSize: number;
  fontFamily: string;
  themeMode: 'light' | 'dark' | 'auto';

  // Suggested questions
  showSuggestions: boolean;
  suggestedQuestions: string[];
  autoSuggestEnabled: boolean;
  autoSuggestCount: number;
}

export const defaultChatWidgetSettings: ChatWidgetSettings = {
  enabled: true,
  position: 'bottom-right',
  size: 'medium',
  primaryColor: '#0070f3',
  secondaryColor: '#ffffff',
  backgroundColor: '#ffffff',
  textColor: '#333333',
  userMessageColor: '#0070f3',
  assistantMessageColor: '#f0f0f0',
  iconType: 'chat',
  headerTitle: '', // Empty = widget uses auto-detected language default
  showBranding: true,
  welcomeMessage: '', // Empty = widget uses auto-detected language default
  inputPlaceholder: '', // Empty = widget uses auto-detected language default
  autoOpen: false,
  autoOpenDelay: 5000,
  showOnMobile: true,
  mobileBreakpoint: 768,
  soundEnabled: false,
  animationType: 'slide',
  borderRadius: '16px',
  zIndex: 9999,
  offsetX: 20,
  offsetY: 20,
  windowWidth: 420,
  windowHeight: 600,
  buttonSize: 60,
  fontFamily: 'system-ui, -apple-system, sans-serif',
  themeMode: 'light',
  showSuggestions: true,
  suggestedQuestions: [],
  autoSuggestEnabled: true,
  autoSuggestCount: 2
};

// ============================================================================
// Session Scoring Types
// ============================================================================

export interface SessionScoringWeights {
  productViews: number;
  timeOnSite: number;
  addToCart: number;
  cartValue: number;
  returnVisit: number;
  scrollDepth: number;
  pageViews: number;
}

export interface SessionScoringThresholds {
  low: number;
  medium: number;
  high: number;
}

export interface SessionScoringSettings {
  enabled: boolean;
  weights: SessionScoringWeights;
  thresholds: SessionScoringThresholds;
}

export const defaultSessionScoring: SessionScoringSettings = {
  enabled: false,
  weights: {
    productViews: 5,
    timeOnSite: 1,
    addToCart: 10,
    cartValue: 0.05,
    returnVisit: 10,
    scrollDepth: 0.08,
    pageViews: 2,
  },
  thresholds: {
    low: 25,
    medium: 50,
    high: 75,
  },
};

// ============================================================================
// Merchant Popup Settings Types
// ============================================================================

export interface MerchantPopupSettings {
  enabled: boolean;
  exitIntentEnabled: boolean;
  exitIntentDiscountCode?: string;
  exitIntentMessage: string;
  highIntentUpsellEnabled: boolean;
  highIntentMinScore: number;
  coldAggressiveness: number;
  warmAggressiveness: number;
  hotAggressiveness: number;
  dynamicProductCount: boolean;
  coldProductCount: number;
  warmProductCount: number;
  hotProductCount: number;
  intentShippingBar: boolean;
  // Discount system
  discountMode: 'manual' | 'auto';
  autoDiscountMin: number;
  autoDiscountMax: number;
  autoDiscountDurationMinutes: number;
  autoDiscountPrefix: string;
  autoDiscountCap: number;
  autoDiscountStrategy: 'rescue' | 'reward';
  // Popup cooldown
  popupCooldownSeconds: number;
  popupMinPageViews: number;
  // Appearance
  popupTitle: string;
  accentColor: string;
  backgroundColor: string;
  textColor: string;
  borderRadius: number;
  maxWidth: number;
  showProductImages: boolean;
  imageSize: number;
  layout: 'list' | 'grid';
  showBrowseLink: boolean;
  browseLinkText: string;
  showBranding: boolean;
  animation: 'bounce' | 'slide' | 'fade';
  overlayOpacity: number;
}

export const defaultPopupSettings: MerchantPopupSettings = {
  enabled: false,
  exitIntentEnabled: true,
  exitIntentDiscountCode: '',
  exitIntentMessage: 'Wait! Get 10% off your order',
  highIntentUpsellEnabled: true,
  highIntentMinScore: 75,
  coldAggressiveness: 30,
  warmAggressiveness: 60,
  hotAggressiveness: 90,
  dynamicProductCount: true,
  coldProductCount: 3,
  warmProductCount: 4,
  hotProductCount: 6,
  intentShippingBar: true,
  // Discount system
  discountMode: 'manual',
  autoDiscountMin: 5,
  autoDiscountMax: 20,
  autoDiscountDurationMinutes: 30,
  autoDiscountPrefix: 'SAVE',
  autoDiscountCap: 15,
  autoDiscountStrategy: 'rescue',
  // Popup cooldown
  popupCooldownSeconds: 15,
  popupMinPageViews: 2,
  // Appearance
  popupTitle: '',
  accentColor: '#3b82f6',
  backgroundColor: '#ffffff',
  textColor: '#111111',
  borderRadius: 16,
  maxWidth: 520,
  showProductImages: true,
  imageSize: 64,
  layout: 'list',
  showBrowseLink: true,
  browseLinkText: 'Browse more products',
  showBranding: true,
  animation: 'bounce',
  overlayOpacity: 55,
};

// ============================================================================
// Smart Cart Drawer Settings
// ============================================================================

export interface CartDrawerSettings {
  enabled: boolean;
  cartTitle: string;
  emptyText: string;
  continueText: string;
  subtotalText: string;
  checkoutText: string;
  shippingNote: string;
  upsellTitle: string;
  freeShippingThreshold: number;
  freeShippingText: string;
  freeShippingMore: string;
  freeShippingAchieved: string;
  interceptAddToCart: boolean;
  openOnAdd: boolean;
}

export const defaultCartDrawerSettings: CartDrawerSettings = {
  enabled: false,
  cartTitle: 'Your Cart',
  emptyText: 'Your cart is empty',
  continueText: 'Continue Shopping',
  subtotalText: 'Subtotal',
  checkoutText: 'Checkout',
  shippingNote: 'Shipping & taxes calculated at checkout',
  upsellTitle: 'You might also like',
  freeShippingThreshold: 0,
  freeShippingText: 'Spend',
  freeShippingMore: 'more for free shipping',
  freeShippingAchieved: 'You qualify for free shipping!',
  interceptAddToCart: true,
  openOnAdd: true,
};
